import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useContatoUpsert } from '@/api/mutations/contato';
import type { Contato, ContatoTelefone, ContatoEmail } from '@/api/types';

const PAPEIS = [
  'dono',
  'socio',
  'ceo',
  'diretor',
  'gerente_geral',
  'gerente_marketing',
  'gerente_comercial',
  'gerente_financeiro',
  'recepcionista',
  'assistente',
  'decisor',
  'influenciador',
  'outro',
];

const TELEFONE_TIPOS = ['whatsapp', 'fixo', 'movel'];
const EMAIL_TIPOS = ['corporativo', 'pessoal', 'gmail'];

interface ContatoFormProps {
  empresaId: string;
  contato?: Contato | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormState {
  nome: string;
  papel: string;
  eh_decisor: boolean;
  cargo_titulo_livre: string;
  linkedin_url: string;
  instagram_url: string;
  notas_livres: string;
  telefones: ContatoTelefone[];
  emails: ContatoEmail[];
}

function emptyState(): FormState {
  return {
    nome: '',
    papel: 'outro',
    eh_decisor: false,
    cargo_titulo_livre: '',
    linkedin_url: '',
    instagram_url: '',
    notas_livres: '',
    telefones: [],
    emails: [],
  };
}

function fromContato(c: Contato): FormState {
  return {
    nome: c.nome,
    papel: c.papel,
    eh_decisor: c.eh_decisor,
    cargo_titulo_livre: c.cargo_titulo_livre ?? '',
    linkedin_url: c.linkedin_url ?? '',
    instagram_url: c.instagram_url ?? '',
    notas_livres: c.notas_livres ?? '',
    telefones: c.telefones ?? [],
    emails: c.emails ?? [],
  };
}

export function ContatoForm({ empresaId, contato, open, onOpenChange }: ContatoFormProps) {
  const upsert = useContatoUpsert();
  const [state, setState] = useState<FormState>(emptyState());

  useEffect(() => {
    if (open) {
      setState(contato ? fromContato(contato) : emptyState());
    }
  }, [open, contato]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }

  function addTelefone() {
    update('telefones', [...state.telefones, { numero: '', tipo: 'whatsapp' }]);
  }
  function patchTelefone(i: number, patch: Partial<ContatoTelefone>) {
    update(
      'telefones',
      state.telefones.map((t, idx) => (idx === i ? { ...t, ...patch } : t)),
    );
  }
  function removeTelefone(i: number) {
    update(
      'telefones',
      state.telefones.filter((_, idx) => idx !== i),
    );
  }

  function addEmail() {
    update('emails', [...state.emails, { endereco: '', tipo: 'corporativo' }]);
  }
  function patchEmail(i: number, patch: Partial<ContatoEmail>) {
    update(
      'emails',
      state.emails.map((e, idx) => (idx === i ? { ...e, ...patch } : e)),
    );
  }
  function removeEmail(i: number) {
    update(
      'emails',
      state.emails.filter((_, idx) => idx !== i),
    );
  }

  async function submit() {
    if (!state.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    const payload = {
      nome: state.nome.trim(),
      papel: state.papel,
      eh_decisor: state.eh_decisor,
      cargo_titulo_livre: state.cargo_titulo_livre.trim() || undefined,
      linkedin_url: state.linkedin_url.trim() || undefined,
      instagram_url: state.instagram_url.trim() || undefined,
      notas_livres: state.notas_livres.trim() || undefined,
      telefones: state.telefones.filter((t) => t.numero.trim()),
      emails: state.emails.filter((e) => e.endereco.trim()),
    };
    try {
      await upsert.mutateAsync({ empresa_id: empresaId, id: contato?.id, payload });
      toast.success(contato ? 'Contato atualizado' : 'Contato criado');
      onOpenChange(false);
    } catch (e) {
      toast.error('Falha ao salvar: ' + (e instanceof Error ? e.message : 'erro'));
    }
  }

  const loading = upsert.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <div className="space-y-4 max-h-[80vh] overflow-y-auto">
          <div>
            <h3 className="text-lg font-semibold">
              {contato ? 'Editar contato' : 'Novo contato'}
            </h3>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Nome *</label>
              <Input
                value={state.nome}
                onChange={(e) => update('nome', e.target.value)}
                placeholder="Nome completo"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Papel</label>
              <select
                value={state.papel}
                onChange={(e) => update('papel', e.target.value)}
                className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              >
                {PAPEIS.map((p) => (
                  <option key={p} value={p}>
                    {p.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">
                Cargo (texto livre)
              </label>
              <Input
                value={state.cargo_titulo_livre}
                onChange={(e) => update('cargo_titulo_livre', e.target.value)}
                placeholder="ex: Diretor Comercial, Gerente de Marketing..."
              />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <input
                type="checkbox"
                id="eh-decisor"
                checked={state.eh_decisor}
                onChange={(e) => update('eh_decisor', e.target.checked)}
                className="h-4 w-4"
              />
              <label htmlFor="eh-decisor" className="text-sm">
                É decisor principal
              </label>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-muted-foreground">Telefones</label>
              <button
                type="button"
                onClick={addTelefone}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Plus className="h-3 w-3" /> adicionar
              </button>
            </div>
            <div className="space-y-1.5">
              {state.telefones.map((t, i) => (
                <div key={i} className="flex gap-1.5">
                  <Input
                    value={t.numero}
                    onChange={(e) => patchTelefone(i, { numero: e.target.value })}
                    placeholder="+55 14 99999-9999"
                    className="flex-1"
                  />
                  <select
                    value={t.tipo ?? 'whatsapp'}
                    onChange={(e) =>
                      patchTelefone(i, { tipo: e.target.value as ContatoTelefone['tipo'] })
                    }
                    className="rounded-md border bg-background px-2 text-xs"
                  >
                    {TELEFONE_TIPOS.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeTelefone(i)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {state.telefones.length === 0 && (
                <p className="text-xs italic text-muted-foreground">— nenhum</p>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-muted-foreground">Emails</label>
              <button
                type="button"
                onClick={addEmail}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Plus className="h-3 w-3" /> adicionar
              </button>
            </div>
            <div className="space-y-1.5">
              {state.emails.map((e, i) => (
                <div key={i} className="flex gap-1.5">
                  <Input
                    type="email"
                    value={e.endereco}
                    onChange={(ev) => patchEmail(i, { endereco: ev.target.value })}
                    placeholder="nome@empresa.com"
                    className="flex-1"
                  />
                  <select
                    value={e.tipo ?? 'corporativo'}
                    onChange={(ev) =>
                      patchEmail(i, { tipo: ev.target.value as ContatoEmail['tipo'] })
                    }
                    className="rounded-md border bg-background px-2 text-xs"
                  >
                    {EMAIL_TIPOS.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeEmail(i)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {state.emails.length === 0 && (
                <p className="text-xs italic text-muted-foreground">— nenhum</p>
              )}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">LinkedIn</label>
              <Input
                value={state.linkedin_url}
                onChange={(e) => update('linkedin_url', e.target.value)}
                placeholder="https://linkedin.com/in/..."
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Instagram</label>
              <Input
                value={state.instagram_url}
                onChange={(e) => update('instagram_url', e.target.value)}
                placeholder="https://instagram.com/..."
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Notas</label>
            <textarea
              value={state.notas_livres}
              onChange={(e) => update('notas_livres', e.target.value)}
              placeholder="Observações sobre esse contato..."
              rows={3}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              {contato ? 'Salvar' : 'Criar contato'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
